/** TanStack Query hooks for notifications: polling list, mark read, and mark all read. */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { NotificationsResponse } from '@formency/shared';

// ─── Query key factory ─────────────────────────────────────────────────────

export const notificationKeys = {
  all: ['f', 'notifications'] as const,
  list: (params?: { unreadOnly?: boolean; limit?: number }) =>
    [...notificationKeys.all, 'list', params] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch notifications for the current user, polling every 60 seconds. */
export function useNotifications(params?: { unreadOnly?: boolean; limit?: number }) {
  return useQuery<NotificationsResponse>({
    queryKey: notificationKeys.list(params),
    queryFn: () =>
      api.get<NotificationsResponse>('/me/notifications', params as Record<string, unknown>),
    refetchInterval: 60_000,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

/** Mark a single notification as read. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      api.patch<void>(`/me/notifications/${notificationId}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Mark all notifications as read. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.patch<void>('/me/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
